import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import Select from "react-select";
import { useAuth } from "./AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faCar,
  faCheckCircle,
  faEdit,
  faEnvelope,
  faFileInvoice,
  faFilter,
  faHistory,
  faPhone,
  faPlus,
  faSearch,
  faSyncAlt,
  faTimesCircle,
  faTrash,
  faUser,
  faEllipsisV,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { Alert, Button, Card, Chip, Tooltip } from "@material-tailwind/react";
import { useNavigate } from "react-router-dom";
import { fixResizeObserverError } from "../../utils/resizeObserverFix";
import { rootUrl } from "../../config/config";
import OwnerModal from "./owners/OwnerModal";
import CreatePortalAccountModal from "../common/CreatePortalAccountModal";

const searchOptions = [
  { value: "name", label: "Name" },
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "id_no", label: "ID Number" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "40px",
    borderRadius: "0.125rem",
    borderColor: state.isFocused ? "#FF5733" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 1px #FF5733" : "none",
    "&:hover": {
      borderColor: "#FF5733",
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#FF5733" : state.isFocused ? "#FFF1EB" : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#1e293b",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 30,
  }),
};

const formatDisplayDate = (date) => {
  if (!date) return "N/A";
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "N/A";
  return parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatCurrency = (value) =>
  (parseFloat(value) || 0).toLocaleString("en-KE", {
    style: "currency",
    currency: "KES",
  });

const Owners = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const authToken = token || localStorage.getItem("authToken") || localStorage.getItem("token") || "";

  const [owners, setOwners] = useState([]);
  const [ownerstatus, setOwnerstatus] = useState([]);
  const [statusLoading, setStatusLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
  const [editId, setEditId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("name");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    dateJoined: "",
  });
  const [openMenu, setOpenMenu] = useState(null);
  const [perms, setPerms] = useState([]);
  const [portalModalOpen, setPortalModalOpen] = useState(false);
  const [portalModalOwner, setPortalModalOwner] = useState(null);

  useEffect(() => fixResizeObserverError(), []);

  const requestConfig = useMemo(
    () => ({
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    }),
    [authToken]
  );

  const fetchOwners = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${rootUrl}/owners`, requestConfig);
      setOwners(Array.isArray(response.data) ? response.data : response.data?.data || []);
      setFailMessage("");
    } catch (error) {
      console.error("Error fetching owners:", error);
      setFailMessage("Failed to fetch owners.");
      setOwners([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      setStatusLoading(true);
      const response = await axios.get(`${rootUrl}/owner-status`, requestConfig);
      if (Array.isArray(response.data)) {
        setOwnerstatus(response.data);
      } else if (Array.isArray(response.data?.data)) {
        setOwnerstatus(response.data.data);
      } else {
        setOwnerstatus([]);
      }
    } catch (error) {
      console.error("Error fetching owner statuses:", error);
      setOwnerstatus([]);
    } finally {
      setStatusLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchOwners();
  }, [authToken]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchStatus();
  }, [authToken]);

  useEffect(() => {
    if (!authToken) return;
    fetch(`${rootUrl}/api/users/me/permissions`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.json())
      .then(j => setPerms(j.data || []))
      .catch(() => setPerms([]));
  }, [authToken]);

  const canManageUsers = () => perms.includes('users.manage') || perms.includes('roles.manage');

  useEffect(() => {
    if (!openMenu) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('[data-owner-actions-menu="true"]')) {
        return;
      }

      if (target.closest('[data-owner-actions-trigger="true"]')) {
        return;
      }

      setOpenMenu(null);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpenMenu(null);
      }
    };

    const handleScrollOrResize = () => {
      setOpenMenu(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [openMenu]);

  const statusOptions = useMemo(
    () =>
      (Array.isArray(ownerstatus) ? ownerstatus : []).map((status) => ({
        value: String(status.id),
        label: status.name?.toUpperCase() || "UNKNOWN",
      })),
    [ownerstatus]
  );

  const summaryStats = useMemo(() => {
    const totalOwners = owners.length;
    const activeOwners = owners.filter((owner) => Number(owner.status_id) === 1).length;
    const inactiveOwners = owners.filter((owner) => Number(owner.status_id) === 2).length;
    const recentOwners = owners.filter((owner) => {
      const joinDate = new Date(owner.date_joined);
      if (Number.isNaN(joinDate.getTime())) return false;
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return joinDate >= thirtyDaysAgo;
    }).length;

    return {
      totalOwners,
      activeOwners,
      inactiveOwners,
      recentOwners,
    };
  }, [owners]);

  const filteredOwners = useMemo(() => {
    let filtered = owners;

    if (searchTerm.trim()) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((owner) => {
        const value = owner?.[searchBy];
        return String(value || "").toLowerCase().includes(lowerCaseSearchTerm);
      });
    }

    if (filters.status) {
      filtered = filtered.filter((owner) => String(owner.status_id) === String(filters.status));
    }

    if (filters.dateJoined) {
      filtered = filtered.filter((owner) => {
        const ownerDate = new Date(owner.date_joined);
        if (Number.isNaN(ownerDate.getTime())) return false;
        return ownerDate.toDateString() === new Date(filters.dateJoined).toDateString();
      });
    }

    return filtered;
  }, [owners, searchTerm, searchBy, filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ status: "", dateJoined: "" });
    setSearchTerm("");
  };

  const openOwnerMenu = (ownerCode, triggerEl) => {
    const rect = triggerEl?.getBoundingClientRect?.();
    if (!rect) {
      setOpenMenu({ ownerCode, top: 0, left: 0, openUp: false });
      return;
    }

    const menuWidth = 176;
    const viewportPadding = 12;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceAbove > 220 && spaceAbove > spaceBelow;

    const left = Math.min(
      window.innerWidth - menuWidth - viewportPadding,
      Math.max(viewportPadding, rect.right - menuWidth)
    );

    setOpenMenu({
      ownerCode,
      top: openUp ? rect.top - 8 : rect.bottom + 8,
      left,
      openUp,
    });
  };

  const renderOwnerActionsMenu = () => {
    if (!openMenu) return null;

    return createPortal(
      <div
        data-owner-actions-menu="true"
        className="fixed w-44 overflow-hidden rounded-md border border-slate-200 bg-white shadow-2xl"
        style={{
          top: `${openMenu.top}px`,
          left: `${openMenu.left}px`,
          transform: openMenu.openUp ? "translateY(-100%)" : "none",
          zIndex: 99999,
        }}
      >
        <button
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
          onClick={() => {
            navigate(`/owners/statements/${openMenu.ownerCode}`);
            setOpenMenu(null);
          }}
        >
          <FontAwesomeIcon icon={faFileInvoice} className="text-xs text-blue-600" />
          <span>Statement</span>
        </button>
        <button
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
          onClick={() => {
            navigate(`/owners/cars/${openMenu.ownerCode}`);
            setOpenMenu(null);
          }}
        >
          <FontAwesomeIcon icon={faCar} className="text-xs text-purple-600" />
          <span>Cars</span>
        </button>
        <button
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
          onClick={() => {
            navigate(`/owners/timeline/${openMenu.ownerCode}`);
            setOpenMenu(null);
          }}
        >
          <FontAwesomeIcon icon={faHistory} className="text-xs text-orange-600" />
          <span>Timeline</span>
        </button>
        <button
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
          onClick={() => {
            const owner = owners.find((item) => item.code === openMenu.ownerCode);
            if (owner) {
              handleEdit(owner);
            }
            setOpenMenu(null);
          }}
        >
          <FontAwesomeIcon icon={faEdit} className="text-xs text-green-600" />
          <span>Edit</span>
        </button>
        <button
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
          onClick={() => {
            handleDelete(openMenu.ownerCode);
            setOpenMenu(null);
          }}
        >
          <FontAwesomeIcon icon={faTrash} className="text-xs text-red-600" />
          <span>Delete</span>
        </button>
      </div>,
      document.body
    );
  };

  const handleDelete = async (code) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this car owner?");
    if (!isConfirmed) return;

    try {
      await axios.delete(`${rootUrl}/owners/${code}`, requestConfig);
      setOwners((prevOwners) => prevOwners.filter((owner) => owner.code !== code));
      setSuccessMessage("Car owner deleted successfully!");
      setFailMessage("");
      setTimeout(() => setSuccessMessage(""), 6000);
    } catch (error) {
      console.error("Error deleting owner:", error);
      setFailMessage("Car owner failed to be deleted!");
    }
  };

  const handleEdit = (owner) => {
    setEditId(owner.code);
    setIsModalOpen(true);
  };

  const openAddModal = () => {
    setEditId(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setEditId(null);
    setIsModalOpen(false);
  };

  const handleModalSuccess = () => {
    fetchOwners();
    setSuccessMessage(editId ? "Owner updated successfully!" : "Owner added successfully!");
    setTimeout(() => setSuccessMessage(""), 3000);
    handleModalClose();
  };

  const getOwnerStatus = (owner) =>
    ownerstatus.find(
      (status) =>
        Number(status.id) === Number(owner.status_id) || Number(status.id) === Number(owner.status)
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
      <div className="p-6 lg:p-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Manage Owners</h1>
          <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
          <p className="text-lg text-[#2a2e30] opacity-80">
            Search, review, and maintain car owner records.
          </p>
        </div>
      </div>

      <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
        {successMessage && (
          <Alert color="green" className="mb-4 rounded-sm text-sm">
            {successMessage}
          </Alert>
        )}
        {failMessage && (
          <Alert color="red" className="mb-4 rounded-sm text-sm">
            {failMessage}
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-2 xl:grid-cols-4">
          <Card className="rounded-xl border border-[#E7D6CA] bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">Total Owners</p>
                <p className="mt-2 text-2xl font-bold text-[#2a2e30]">{summaryStats.totalOwners}</p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#FFF1EB] text-[#FF5733]">
                <FontAwesomeIcon icon={faUser} />
              </div>
            </div>
          </Card>

          <Card className="rounded-xl border border-[#E7D6CA] bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">Active Owners</p>
                <p className="mt-2 text-2xl font-bold text-[#2a2e30]">{summaryStats.activeOwners}</p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-green-50 text-green-600">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
            </div>
          </Card>

          <Card className="rounded-xl border border-[#E7D6CA] bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">Inactive Owners</p>
                <p className="mt-2 text-2xl font-bold text-[#2a2e30]">{summaryStats.inactiveOwners}</p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-red-50 text-red-600">
                <FontAwesomeIcon icon={faTimesCircle} />
              </div>
            </div>
          </Card>

          <Card className="rounded-xl border border-[#E7D6CA] bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">Recent (30 days)</p>
                <p className="mt-2 text-2xl font-bold text-[#2a2e30]">{summaryStats.recentOwners}</p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                <FontAwesomeIcon icon={faCalendarAlt} />
              </div>
            </div>
          </Card>
        </div>

        <Card className="mb-4 rounded-xl border border-[#E7D6CA] bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => setShowFilters((prev) => !prev)}
                className="bg-slate-100 px-3 py-1 text-sm text-slate-700 shadow-none rounded-sm hover:bg-slate-200"
              >
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faFilter} />
                  Filters
                </span>
              </Button>
              <Button
                onClick={fetchOwners}
                className="bg-[#FF5733] px-3 py-1 text-sm font-semibold text-white shadow-none rounded-sm hover:bg-[#e64520]"
              >
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faSyncAlt} />
                  Refresh
                </span>
              </Button>
              <Button
                onClick={openAddModal}
                className="bg-[#FF5733] px-3 py-1 text-sm font-semibold text-white shadow-none rounded-sm hover:bg-[#e64520]"
              >
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faPlus} />
                  New Owner
                </span>
              </Button>
            </div>

            <div className="text-sm text-slate-600">
              Showing {filteredOwners.length} of {summaryStats.totalOwners} owners
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Search By</label>
              <Select
                options={searchOptions}
                value={searchOptions.find((option) => option.value === searchBy)}
                onChange={(option) => setSearchBy(option?.value || "name")}
                styles={selectStyles}
                isSearchable={false}
              />
            </div>

            <div>
              <label htmlFor="owner-search" className="mb-1 block text-sm font-semibold text-slate-700">
                Search Term
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  id="owner-search"
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={`Search by ${searchOptions.find((option) => option.value === searchBy)?.label || "Name"}`}
                  className="w-full rounded-sm border border-slate-300 bg-white py-2 pl-10 pr-3 text-sm text-slate-800 focus:border-[#FF5733] focus:outline-none focus:ring-1 focus:ring-[#FF5733]"
                />
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 border-t border-[#EFE3DB] pt-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">Status</label>
                  <Select
                    options={[{ value: "", label: "All Status" }, ...statusOptions]}
                    value={
                      [{ value: "", label: "All Status" }, ...statusOptions].find(
                        (option) => option.value === String(filters.status)
                      ) || { value: "", label: "All Status" }
                    }
                    onChange={(option) => handleFilterChange("status", option?.value || "")}
                    styles={selectStyles}
                    isLoading={statusLoading}
                    isDisabled={statusLoading}
                    isSearchable
                  />
                </div>

                <div>
                  <label htmlFor="joined-date" className="mb-1 block text-sm font-semibold text-slate-700">
                    Date Joined
                  </label>
                  <input
                    id="joined-date"
                    type="date"
                    value={filters.dateJoined}
                    onChange={(event) => handleFilterChange("dateJoined", event.target.value)}
                    className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-[#FF5733] focus:outline-none focus:ring-1 focus:ring-[#FF5733]"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button
                  onClick={clearFilters}
                  className="bg-slate-100 px-3 py-1 text-sm text-slate-700 shadow-none hover:bg-slate-200"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        <div className="space-y-4 md:hidden">
          {loading ? (
            <Card className="rounded-xl border border-[#E7D6CA] bg-white p-6 text-center shadow-sm">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-[#FF5733] border-t-transparent"></div>
            </Card>
          ) : filteredOwners.length === 0 ? (
            <Card className="rounded-xl border border-[#E7D6CA] bg-white p-6 text-center shadow-sm">
              <p className="text-sm text-slate-600">No owners found.</p>
            </Card>
          ) : (
            filteredOwners.map((owner) => {
              const foundStatus = getOwnerStatus(owner);
              return (
                <Card key={owner.code} className="rounded-xl border border-[#E7D6CA] bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-[#2a2e30]">{owner.name}</p>
                      <p className="text-xs text-slate-500">Code: {owner.code}</p>
                      <p className="text-xs text-slate-500">ID: {owner.id_no || "N/A"}</p>
                    </div>
                    <Chip
                      variant="ghost"
                      size="sm"
                      value={foundStatus?.name?.toUpperCase() || "UNKNOWN"}
                      color={foundStatus?.active ? "green" : foundStatus ? "red" : "gray"}
                    />
                  </div>

                  <div className="mt-4 space-y-2 text-sm text-slate-600">
                    <p className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faPhone} className="text-slate-400" />
                      {owner.phone || "N/A"}
                    </p>
                    <p className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faEnvelope} className="text-slate-400" />
                      {owner.email || "N/A"}
                    </p>
                    <p>Balance: <span className="font-semibold text-slate-800">{formatCurrency(owner.bal)}</span></p>
                    <p>Date Joined: {formatDisplayDate(owner.date_joined)}</p>
                  </div>

                  <div className="mt-3 flex items-center gap-2">
                    {owner.portal_user_id ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        {owner.portal_first_login_at ? 'Account Active' : 'Has Account'}
                      </span>
                    ) : canManageUsers() ? (
                      <button
                        onClick={() => { setPortalModalOwner(owner); setPortalModalOpen(true); }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 border border-orange-300 rounded-full px-2 py-0.5 hover:bg-orange-50 transition"
                      >
                        <FontAwesomeIcon icon={faUserPlus} className="text-xs" />
                        Create Account
                      </button>
                    ) : null}
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center justify-between">
                      <Button
                        onClick={() => navigate(`/owners/statements/${owner.code}`)}
                        className="bg-slate-100 px-3 py-2 text-sm text-slate-700 shadow-none hover:bg-slate-200"
                      >
                        Statement
                      </Button>
                      <Button
                        onClick={() => navigate(`/owners/cars/${owner.code}`)}
                        className="bg-slate-100 px-3 py-2 text-sm text-slate-700 shadow-none hover:bg-slate-200"
                      >
                        Cars
                      </Button>
                      <div className="relative z-50">
                        <Button
                          size="sm"
                          variant="text"
                          onClick={(e) => {
                            if (openMenu?.ownerCode === owner.code) {
                              setOpenMenu(null);
                            } else {
                              openOwnerMenu(owner.code, e.currentTarget);
                            }
                          }}
                          data-owner-actions-trigger="true"
                          className="p-2 rounded-full w-10 h-10 flex items-center justify-center border border-slate-200 text-slate-600 hover:bg-slate-50"
                          aria-label="Actions"
                        >
                          <FontAwesomeIcon icon={faEllipsisV} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>

        <Card className="hidden overflow-visible rounded-xl border border-[#E7D6CA] bg-white shadow-sm md:block">
          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#FF5733] border-t-transparent"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[780px] w-full table-fixed text-left">
                <thead>
                  <tr className="border-b border-[#EFE3DB] bg-[#FFF8F5]">
                    <th className="w-[100px] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Code</th>
                    <th className="w-[200px] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Owner</th>
                    <th className="w-[200px] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Contact</th>
                    <th className="w-[48px] px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status</th>
                    <th className="w-[48px] px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">Invoice</th>
                    <th className="w-[110px] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Joined</th>
                    <th className="w-[48px] px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">Portal</th>
                    <th className="w-[56px] px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOwners.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="p-8 text-center text-xs text-slate-600">
                        No owners found.
                      </td>
                    </tr>
                  ) : (
                    filteredOwners.map((owner) => {
                      const foundStatus = getOwnerStatus(owner);
                      return (
                        <tr key={owner.code} className="border-b border-slate-100 text-xs hover:bg-orange-50/60 hover:border-l-2 hover:border-l-[#FF5733] cursor-pointer transition-colors" onClick={() => navigate(`/owners/statements/${owner.code}`)}>
                          <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{owner.code}</td>
                          <td className="px-3 py-2">
                            <p className="truncate font-semibold text-slate-800">{owner.name}</p>
                            <p className="truncate text-[10px] text-slate-400">ID: {owner.id_no || "N/A"}</p>
                          </td>
                          <td className="px-3 py-2">
                            <p className="truncate text-slate-700">{owner.phone || "N/A"}</p>
                            <p className="truncate text-[10px] text-slate-400">{owner.email || "N/A"}</p>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Tooltip content={foundStatus?.name?.toUpperCase() || "UNKNOWN"}>
                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${foundStatus?.active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                                <FontAwesomeIcon icon={foundStatus?.active ? faCheckCircle : faTimesCircle} className="text-xs" />
                              </span>
                            </Tooltip>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Tooltip content={owner.is_invoiced ? "Invoiced" : "Not invoiced"}>
                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${owner.is_invoiced ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-500'}`}>
                                <FontAwesomeIcon icon={owner.is_invoiced ? faCheckCircle : faTimesCircle} className="text-xs" />
                              </span>
                            </Tooltip>
                          </td>
                          <td className="px-3 py-2 text-slate-700">{formatDisplayDate(owner.date_joined)}</td>
                          <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                            {owner.portal_user_id ? (
                              <Tooltip content={owner.portal_first_login_at ? 'Portal active' : 'Account created, not yet activated'}>
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${owner.portal_first_login_at ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-500'}`}>
                                  <FontAwesomeIcon icon={owner.portal_first_login_at ? faCheckCircle : faUserPlus} className="text-sm" />
                                </span>
                              </Tooltip>
                            ) : canManageUsers() ? (
                              <Tooltip content="Create portal account">
                                <button
                                  onClick={() => { setPortalModalOwner(owner); setPortalModalOpen(true); }}
                                  className="inline-flex items-center justify-center w-7 h-7 rounded-full text-orange-500 hover:bg-orange-50 transition"
                                  aria-label="Create portal account"
                                >
                                  <FontAwesomeIcon icon={faUserPlus} className="text-sm" />
                                </button>
                              </Tooltip>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                          <td className="p-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center gap-1">
                              <div className="relative z-50">
                                <Button
                                  size="sm"
                                  variant="text"
                                  onClick={(e) => {
                                    if (openMenu?.ownerCode === owner.code) {
                                      setOpenMenu(null);
                                    } else {
                                      openOwnerMenu(owner.code, e.currentTarget);
                                    }
                                  }}
                                  data-owner-actions-trigger="true"
                                  className="w-8 h-8 p-2 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"
                                  aria-label="Actions"
                                >
                                  <FontAwesomeIcon icon={faEllipsisV} className="text-sm" />
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <OwnerModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          editData={editId ? owners.find((owner) => owner.code === editId) : null}
          ownerstatus={ownerstatus}
          statusLoading={statusLoading}
        />
        <CreatePortalAccountModal
          isOpen={portalModalOpen}
          onClose={() => { setPortalModalOpen(false); setPortalModalOwner(null); }}
          onSuccess={() => { fetchOwners(); setPortalModalOpen(false); setPortalModalOwner(null); }}
          actorType="owner"
          entityCode={portalModalOwner?.code}
          prefill={{ name: portalModalOwner?.name, email: portalModalOwner?.email }}
          token={authToken}
        />
        {renderOwnerActionsMenu()}
      </div>
    </div>
  );
};

export default Owners;
